<script>
import { computed } from 'vue';
import { useAlert } from 'dashboard/composables';
import EmojiOrIcon from 'shared/components/EmojiOrIcon.vue';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import NextButton from 'dashboard/components-next/button/Button.vue';
import {
  maskEmail,
  maskPhoneNumber,
  createEncryptedContactLink,
  handleEncryptedContactClick,
} from 'shared/helpers/contactPrivacyHelper';

export default {
  components: {
    EmojiOrIcon,
    NextButton,
  },
  props: {
    href: {
      type: String,
      default: '',
    },
    icon: {
      type: String,
      required: true,
    },
    emoji: {
      type: String,
      required: true,
    },
    value: {
      type: String,
      default: '',
    },
    showCopy: {
      type: Boolean,
      default: false,
    },
    contact: {
      type: Object,
      default: () => ({}),
    },
  },
  computed: {
    isEmail() {
      return this.value && this.value.includes('@');
    },
    isPhone() {
      return (
        this.value &&
        !this.value.includes('@') &&
        /^\+?[\d\s\-()]+$/.test(this.value)
      );
    },
    maskedValue() {
      if (!this.value) return '';
      if (this.isEmail) return maskEmail(this.value);
      if (this.isPhone) return maskPhoneNumber(this.value);
      return this.value;
    },
    encryptedLink() {
      if (!this.value) return '';
      const customAttributes = this.contact?.custom_attributes || {};
      const additionalAttributes = this.contact?.additional_attributes || {};
      
      if (this.isEmail) {
        return createEncryptedContactLink('email', this.value, {
          custom_attributes: customAttributes,
          additional_attributes: additionalAttributes,
        });
      }
      if (this.isPhone) {
        return createEncryptedContactLink('phone', this.value, {
          custom_attributes: customAttributes,
          additional_attributes: additionalAttributes,
        });
      }
      return '';
    },
  },
  methods: {
    async onCopy(e) {
      e.preventDefault();
      e.stopPropagation();
      // Instead of copying, send POST request
      if (this.encryptedLink) {
        await handleEncryptedContactClick(this.encryptedLink);
        useAlert(this.$t('CONTACT_PANEL.COPY_SUCCESSFUL'));
      } else {
        // Fallback to actual copy if no encrypted link
        await copyTextToClipboard(this.value);
        useAlert(this.$t('CONTACT_PANEL.COPY_SUCCESSFUL'));
      }
    },
    handleContactClick(e) {
      e.preventDefault();
      if (this.encryptedLink) {
        handleEncryptedContactClick(this.encryptedLink);
      }
    },
  },
};
</script>

<template>
  <div class="w-full h-5 ltr:-ml-1 rtl:-mr-1">
    <a
      v-if="encryptedLink"
      href="#"
      class="flex items-center gap-2 text-n-slate-11 hover:underline cursor-pointer"
      :data-encrypted-link="encryptedLink"
      @click="handleContactClick"
    >
      <EmojiOrIcon
        :icon="icon"
        :emoji="emoji"
        icon-size="14"
        class="flex-shrink-0 ltr:ml-1 rtl:mr-1"
      />
      <span
        v-if="value"
        class="overflow-hidden text-sm whitespace-nowrap text-ellipsis"
        :title="maskedValue"
      >
        {{ maskedValue }}
      </span>
      <span v-else class="text-sm text-n-slate-11">
        {{ $t('CONTACT_PANEL.NOT_AVAILABLE') }}
      </span>
      <NextButton
        v-if="showCopy"
        ghost
        xs
        slate
        class="ltr:-ml-1 rtl:-mr-1"
        icon="i-lucide-clipboard"
        @click="onCopy"
      />
    </a>

    <a
      v-else-if="href && !encryptedLink"
      :href="href"
      class="flex items-center gap-2 text-n-slate-11 hover:underline"
    >
      <EmojiOrIcon
        :icon="icon"
        :emoji="emoji"
        icon-size="14"
        class="flex-shrink-0 ltr:ml-1 rtl:mr-1"
      />
      <span
        v-if="value"
        class="overflow-hidden text-sm whitespace-nowrap text-ellipsis"
        :title="value"
      >
        {{ value }}
      </span>
      <span v-else class="text-sm text-n-slate-11">
        {{ $t('CONTACT_PANEL.NOT_AVAILABLE') }}
      </span>
      <NextButton
        v-if="showCopy"
        ghost
        xs
        slate
        class="ltr:-ml-1 rtl:-mr-1"
        icon="i-lucide-clipboard"
        @click="onCopy"
      />
    </a>

    <div v-else class="flex items-center gap-2 text-n-slate-11">
      <EmojiOrIcon
        :icon="icon"
        :emoji="emoji"
        icon-size="14"
        class="flex-shrink-0 ltr:ml-1 rtl:mr-1"
      />
      <span
        v-if="value"
        v-dompurify-html="value"
        class="overflow-hidden text-sm whitespace-nowrap text-ellipsis"
      />
      <span v-else class="text-sm text-n-slate-11">
        {{ $t('CONTACT_PANEL.NOT_AVAILABLE') }}
      </span>
    </div>
  </div>
</template>
